import { useState, useMemo, useCallback } from "react";

export function useFolderNavigation(initialData, initialNavigationState = null) {
  const [rootData, setRootData] = useState(initialData);
  const [searchFilter, setSearchFilter] = useState("");
  const [navigationState, setNavigationState] = useState(
    initialNavigationState || {
      currentPath: [],
      currentItems: initialData,
      breadcrumbs: [{ name: "Inicio", path: [] }],
    }
  );

  // Función para actualizar los datos manteniendo la navegación
  const updateData = useCallback((newData) => {
    setRootData(newData);
    
    // Si no estamos navegando (estamos en la raíz), actualizar simplemente
    if (navigationState.currentPath.length === 0) {
      setNavigationState(prev => ({
        ...prev,
        currentItems: newData
      }));
      return;
    }

    // Función para encontrar un nodo por path en los nuevos datos
    const findCurrentItemsByPath = (data, path) => {
      let currentLevel = data;
      
      for (const key of path) {
        const foundItem = currentLevel.find(item => item.key === key);
        if (!foundItem || !foundItem.children) {
          return null; // El path ya no existe en los nuevos datos
        }
        currentLevel = foundItem.children;
      }
      
      return currentLevel;
    };

    // Buscar los items actuales en los nuevos datos
    const newCurrentItems = findCurrentItemsByPath(newData, navigationState.currentPath);
    
    if (newCurrentItems) {
      // El path sigue existiendo, mantener la navegación
      setNavigationState(prev => ({
        ...prev,
        currentItems: newCurrentItems
      }));
    } else {
      // El path ya no existe, buscar el path más largo que sí existe
      let validPath = [];
      let validItems = newData;
      
      for (let i = 0; i < navigationState.currentPath.length; i++) {
        const testPath = navigationState.currentPath.slice(0, i + 1);
        const testItems = findCurrentItemsByPath(newData, testPath);
        
        if (testItems) {
          validPath = testPath;
          validItems = testItems;
        } else {
          break;
        }
      }
      
      // Reconstruir breadcrumbs para el path válido
      const validBreadcrumbs = [{ name: "Inicio", path: [] }];
      let currentLevel = newData;
      
      for (let i = 0; i < validPath.length; i++) {
        const pathKey = validPath[i];
        const foundItem = currentLevel.find(item => item.key === pathKey);
        
        if (foundItem) {
          validBreadcrumbs.push({
            name: foundItem.viewName || foundItem.name,
            path: validPath.slice(0, i + 1)
          });
          currentLevel = foundItem.children || [];
        }
      }
      setNavigationState({
        currentPath: validPath,
        currentItems: validItems,
        breadcrumbs: validBreadcrumbs,
      });
    }
  }, [navigationState.currentPath]);

  const filteredItems = useMemo(() => {
    if (!searchFilter.trim()) return navigationState.currentItems;

    const filtered = navigationState.currentItems.filter(
      (item) =>
        item.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (item.viewName && item.viewName.toLowerCase().includes(searchFilter.toLowerCase()))
    );
        return filtered;
  }, [navigationState.currentItems, searchFilter]);

  const navigateToFolder = useCallback((item) => {
  if (item.children && item.children.length > 0) {
 

    const newPath = [...navigationState.currentPath, item.key];



    const newBreadcrumbs = [
      ...navigationState.breadcrumbs,
      { name: item.viewName || item.name, path: newPath },
    ];

    setNavigationState({
      currentPath: newPath,
      currentItems: item.children,
      breadcrumbs: newBreadcrumbs,
    });

  }
}, []);

  const navigateBack = useCallback(() => {
    if (navigationState.currentPath.length === 0) {
      return;
    }

    const newPath = navigationState.currentPath.slice(0, -1);
    const newBreadcrumbs = navigationState.breadcrumbs.slice(0, -1);

    let items = rootData;
    for (const pathKey of newPath) {
      const foundItem = items.find((item) => item.key === pathKey);
      if (foundItem?.children) items = foundItem.children;
    }
    setNavigationState({
      currentPath: newPath,
      currentItems: items,
      breadcrumbs: newBreadcrumbs,
    });
  }, [navigationState.currentPath, navigationState.breadcrumbs, rootData]);

  const navigateToBreadcrumb = useCallback((targetPath) => {
    let items = rootData;
    for (const pathKey of targetPath) {
      const foundItem = items.find((item) => item.key === pathKey);
      if (foundItem?.children) items = foundItem.children;
    }

    const breadcrumbIndex = navigationState.breadcrumbs.findIndex(
      (b) => JSON.stringify(b.path) === JSON.stringify(targetPath)
    );

    setNavigationState({
      currentPath: targetPath,
      currentItems: items,
      breadcrumbs: navigationState.breadcrumbs.slice(0, breadcrumbIndex + 1),
    });
  }, [navigationState.breadcrumbs, rootData]);

  const updateFileCount = useCallback((itemKey, newCount) => {
    const updateItemRecursively = (items) => {
      return items.map((item) => {
        if (item.key === itemKey) return { ...item, fileCount: newCount };
        if (item.children) return { ...item, children: updateItemRecursively(item.children) };
        return item;
      });
    };

    const updatedData = updateItemRecursively(rootData);
    setRootData(updatedData);

    const updatedCurrentItems = updateItemRecursively(navigationState.currentItems);
    setNavigationState((prev) => ({ ...prev, currentItems: updatedCurrentItems }));
  }, [rootData, navigationState.currentItems]);

  return {
    navigationState,
    filteredItems,
    searchFilter,
    setSearchFilter,
    navigateToFolder,
    navigateBack,
    navigateToBreadcrumb,
    canNavigateBack: navigationState.currentPath.length > 0,
    updateFileCount,
    updateData,
    rootData,
  };
}